import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  ShieldCheck, Lock, LifeBuoy, ScrollText, KeyRound, Building2, TrendingUp,
  AlertTriangle, Download, MapPin, Activity, Gauge, Bell,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKCardHeader, VLKCardTitle, VLKCardContent, VLKCardDescription,
  VLKKPICard, VLKSelect, VLKButton, VLKTabs, VLKDataTable, VLKStatusBadge, VLKRiskMeter, VLKSkeleton,
} from "@/components/vlk";

const CHART_COLORS = ["hsl(var(--chart-1))","hsl(var(--chart-2))","hsl(var(--chart-3))","hsl(var(--chart-4))","hsl(var(--chart-5))"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DeltaBadge({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className={"font-mono text-[10px] tabular " + (up ? "text-success" : "text-destructive")}>
      {up ? "+" : ""}{pct}%
    </span>
  );
}

function ChartCard({ title, description, children, height = 260 }) {
  return (
    <VLKCard>
      <VLKCardHeader>
        <VLKCardTitle>{title}</VLKCardTitle>
        {description && <VLKCardDescription>{description}</VLKCardDescription>}
      </VLKCardHeader>
      <VLKCardContent>
        <div style={{ height, minWidth: 300 }}>{children}</div>
      </VLKCardContent>
    </VLKCard>
  );
}

/* -------- Overview -------- */
function OverviewTab({ days }) {
  const { data: ov } = useQuery({ queryKey: ["an-overview", days], queryFn: () => api.get(`/analytics/overview?days=${days}`).then(r => r.data) });
  const { data: locks } = useQuery({ queryKey: ["an-locks", days], queryFn: () => api.get(`/analytics/locks?days=${days}`).then(r => r.data) });
  const { data: growth } = useQuery({ queryKey: ["an-growth", days], queryFn: () => api.get(`/analytics/growth?days=${days}`).then(r => r.data) });
  const k = ov?.kpis || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <VLKKPICard label="Total identities" value={k.identities_total?.value ?? "—"} icon={ShieldCheck} testId="an-kpi-identities" />
        <VLKKPICard label="New this window" value={k.new_identities?.value ?? "—"} icon={TrendingUp}
                    delta={k.new_identities?.delta_pct} tone="success" />
        <VLKKPICard label="Locked identities" value={k.identities_locked?.value ?? "—"} icon={Lock} tone="warning" />
        <VLKKPICard label="High-risk events" value={k.high_risk_events?.value ?? "—"} icon={AlertTriangle} tone="danger" />
        <VLKKPICard label="Recoveries reactivated" value={k.recoveries_completed?.value ?? "—"} icon={LifeBuoy} tone="success" />
        <VLKKPICard label="Recovery in-flight" value={k.recoveries_in_flight?.value ?? "—"} icon={LifeBuoy} tone="info" />
        <VLKKPICard label="API calls · 24h" value={k.api_calls?.value?.toLocaleString?.() ?? "—"} icon={Activity} />
        <VLKKPICard label="API error rate" value={k.api_error_rate_pct?.value != null ? `${k.api_error_rate_pct.value}%` : "—"} icon={Gauge}
                    tone={(k.api_error_rate_pct?.value || 0) > 2 ? "danger" : "success"} />
      </div>

      <ChartCard title="Identity growth · new registrations per day" description={`Trailing ${days} days.`}>
        {growth ? (
          <ResponsiveContainer width="100%" height="100%" minHeight={220}>
            <LineChart data={growth.buckets}>
              <XAxis dataKey="day" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
              <Line type="monotone" dataKey="new_identities" stroke="hsl(var(--foreground))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : <VLKSkeleton className="w-full h-full" />}
      </ChartCard>

      <ChartCard title="Lock / unlock activity" description="Derived from immutable audit ledger.">
        {locks ? (
          <ResponsiveContainer width="100%" height="100%" minHeight={220}>
            <BarChart data={locks.buckets}>
              <XAxis dataKey="day" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <Bar dataKey="locks" fill="hsl(var(--destructive))" radius={0} />
              <Bar dataKey="unlocks" fill="hsl(var(--success))" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        ) : <VLKSkeleton className="w-full h-full" />}
      </ChartCard>
    </div>
  );
}

/* -------- Recovery -------- */
function RecoveryTab() {
  const { data } = useQuery({ queryKey: ["an-recovery"], queryFn: () => api.get("/analytics/recovery").then(r => r.data) });
  const f = data?.funnel || {};
  const funnelData = ["SUBMITTED", "AWAITING_ATTESTATION", "AWAITING_APPROVAL", "APPROVED", "REACTIVATED", "REJECTED"]
    .map(s => ({ state: s.replace(/_/g, " "), count: f[s] || 0 }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <VLKKPICard label="Completion rate" value={data?.completion_rate_pct != null ? `${data.completion_rate_pct}%` : "—"} icon={LifeBuoy} tone="success" />
        <VLKKPICard label="Avg time to reactivate" value={data?.avg_time_to_reactivate_hours != null ? `${data.avg_time_to_reactivate_hours}h` : "—"} icon={Activity} />
        <VLKKPICard label="Reactivated" value={f.REACTIVATED ?? "—"} icon={LifeBuoy} tone="info" />
        <VLKKPICard label="Rejected" value={f.REJECTED ?? "—"} icon={AlertTriangle} tone="danger" />
      </div>
      <ChartCard title="Recovery pipeline funnel">
        {data ? (
          <ResponsiveContainer width="100%" height="100%" minHeight={220}>
            <BarChart data={funnelData} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
              <YAxis dataKey="state" type="category" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} width={140} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
              <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        ) : <VLKSkeleton className="w-full h-full" />}
      </ChartCard>
    </div>
  );
}

/* -------- Institutions scorecards -------- */
function InstitutionsTab() {
  const { data } = useQuery({ queryKey: ["an-institutions"], queryFn: () => api.get("/analytics/institutions").then(r => r.data) });
  return (
    <VLKCard>
      <VLKCardHeader>
        <VLKCardTitle>Institution scorecards</VLKCardTitle>
        <VLKCardDescription>Ranked by high-risk events, then by verification volume.</VLKCardDescription>
      </VLKCardHeader>
      <VLKCardContent className="p-0">
        <VLKDataTable
          columns={[
            { key: "name", header: "Institution", render: (r) => <div><div className="font-medium">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.vlk_inst_id}</div></div> },
            { key: "type", header: "Type", render: (r) => <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{r.type}</span> },
            { key: "status", header: "Status", render: (r) => <VLKStatusBadge status={r.status.toLowerCase()}>{r.status}</VLKStatusBadge> },
            { key: "verifications", header: "Verifs", align: "right", mono: true, render: (r) => <span className="tabular">{r.verifications.toLocaleString()}</span> },
            { key: "audit_events", header: "Audit", align: "right", mono: true, render: (r) => <span className="tabular">{r.audit_events}</span> },
            { key: "high_risk_events", header: "High risk", align: "right", mono: true, render: (r) => <span className={"tabular " + (r.high_risk_events > 0 ? "text-destructive" : "text-muted-foreground")}>{r.high_risk_events}</span> },
            { key: "active_credentials", header: "Active keys", align: "right", mono: true, render: (r) => <span className="tabular">{r.active_credentials}</span> },
            { key: "api_calls_24h", header: "API 24h", align: "right", mono: true, render: (r) => <span className="tabular">{r.api_calls_24h.toLocaleString()}</span> },
          ]}
          rows={data?.scorecards || []}
          rowKey={(r) => r.vlk_inst_id}
          empty="No institutions yet."
        />
      </VLKCardContent>
    </VLKCard>
  );
}

/* -------- Geography -------- */
function GeographyTab() {
  const { data } = useQuery({ queryKey: ["an-geography"], queryFn: () => api.get("/analytics/geography").then(r => r.data) });
  const rows = data?.regions || [];
  const max = Math.max(1, ...rows.map(r => r.total));
  return (
    <VLKCard>
      <VLKCardHeader>
        <VLKCardTitle>Geographic activity</VLKCardTitle>
        <VLKCardDescription>Identity distribution by state / country with risk overlay.</VLKCardDescription>
      </VLKCardHeader>
      <VLKCardContent>
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.location} className="grid grid-cols-[160px_1fr_auto] items-center gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate">{r.location}</span>
              </div>
              <div className="relative h-6 bg-secondary/50 rounded-sm overflow-hidden border border-border">
                <div className="absolute inset-y-0 left-0 bg-foreground/70" style={{ width: `${(r.total / max) * 100}%` }} />
                {r.risk_high > 0 && (
                  <div className="absolute inset-y-0 left-0 bg-destructive/80" style={{ width: `${(r.risk_high / max) * 100}%` }} />
                )}
              </div>
              <div className="font-mono text-xs tabular text-right whitespace-nowrap">
                <span className="text-foreground">{r.total}</span>
                {r.risk_high > 0 && <span className="text-destructive ml-2">· {r.risk_high} hi-risk</span>}
                {r.locked > 0 && <span className="text-warning ml-2">· {r.locked} locked</span>}
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="text-sm text-muted-foreground">No geographic data.</div>}
        </div>
      </VLKCardContent>
    </VLKCard>
  );
}

/* -------- Risk & Compliance -------- */
function RiskComplianceTab({ days }) {
  const { data: risk } = useQuery({ queryKey: ["an-risk", days], queryFn: () => api.get(`/analytics/risk?days=${days}`).then(r => r.data) });
  const { data: comp } = useQuery({ queryKey: ["an-compliance", days], queryFn: () => api.get(`/analytics/compliance?days=${days}`).then(r => r.data) });
  const dist = risk?.risk_distribution || {};
  const distData = Object.entries(dist).map(([name, value]) => ({ name, value }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <VLKKPICard label="Denial rate" value={risk?.denial_rate_pct != null ? `${risk.denial_rate_pct}%` : "—"} icon={AlertTriangle} tone={(risk?.denial_rate_pct || 0) > 5 ? "danger" : "info"} />
        <VLKKPICard label="High-risk events" value={comp?.high_risk_events ?? "—"} icon={AlertTriangle} tone="warning" />
        <VLKKPICard label="Integrity hash coverage" value={comp?.integrity_hash_coverage_pct != null ? `${comp.integrity_hash_coverage_pct}%` : "—"} icon={ShieldCheck} tone="success" />
        <VLKKPICard label="Retention" value={comp?.retention_days ? `${Math.round(comp.retention_days / 365)} yr` : "—"} icon={ScrollText} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Identity risk distribution">
          {risk ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <PieChart>
                <Pie data={distData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={1}>
                  {distData.map((r, i) => <Cell key={r.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <VLKSkeleton className="w-full h-full" />}
        </ChartCard>
        <VLKCard>
          <VLKCardHeader>
            <VLKCardTitle>Top threat actions</VLKCardTitle>
            <VLKCardDescription>High + critical risk audit events by action.</VLKCardDescription>
          </VLKCardHeader>
          <VLKCardContent>
            {(risk?.top_threats || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">No high-risk activity in window.</div>
            ) : (
              <div className="space-y-2">
                {risk.top_threats.map(t => (
                  <div key={t.action} className="flex items-center justify-between gap-2 border-b border-border/60 last:border-0 py-1.5">
                    <span className="font-mono text-xs">{t.action}</span>
                    <span className="font-mono text-xs tabular text-destructive">{t.count}</span>
                  </div>
                ))}
              </div>
            )}
          </VLKCardContent>
        </VLKCard>
      </div>
    </div>
  );
}

/* -------- System Health -------- */
function SystemHealthTab() {
  const { data: sys } = useQuery({ queryKey: ["an-system"], queryFn: () => api.get("/analytics/system-health").then(r => r.data) });
  const { data: notif } = useQuery({ queryKey: ["an-notif"], queryFn: () => api.get("/analytics/notification-delivery").then(r => r.data) });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <VLKKPICard label="Uptime · 30d" value={sys?.uptime_pct_30d != null ? `${sys.uptime_pct_30d}%` : "—"} icon={Activity} tone="success" />
        <VLKKPICard label="Avg latency" value={sys?.avg_latency_ms != null ? `${sys.avg_latency_ms} ms` : "—"} icon={Gauge} tone={(sys?.avg_latency_ms || 0) > 200 ? "warning" : "info"} />
        <VLKKPICard label="P95 latency" value={sys?.p95_latency_ms != null ? `${sys.p95_latency_ms} ms` : "—"} icon={Gauge} />
        <VLKKPICard label="Error budget" value={sys?.error_budget_remaining_pct != null ? `${sys.error_budget_remaining_pct}%` : "—"} icon={ShieldCheck} tone={(sys?.error_budget_remaining_pct || 100) < 50 ? "danger" : "success"} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <VLKKPICard label="Notifications delivered" value={notif?.delivered_total?.toLocaleString?.() ?? "—"} icon={Bell} />
        <VLKKPICard label="Read %" value={notif?.read_pct != null ? `${notif.read_pct}%` : "—"} icon={Bell} tone="info" />
        <VLKKPICard label="High priority" value={notif?.high_priority ?? "—"} icon={AlertTriangle} tone="warning" />
      </div>
    </div>
  );
}

/* ============================== Main page ============================== */
export default function Analytics() {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("overview");
  const exportUrl = `${BACKEND_URL}/api/analytics/export.csv?days=${days}`;

  return (
    <div>
      <VLKPageHeader
        title="Executive analytics"
        description="Board-grade view of identity, security, compliance, and platform posture."
        actions={
          <div className="flex items-center gap-2">
            <VLKSelect value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="an-range" className="w-[160px]">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
            </VLKSelect>
            <a href={exportUrl} data-testid="an-export">
              <VLKButton variant="outline"><Download className="h-4 w-4" />Export CSV</VLKButton>
            </a>
          </div>
        }
      />

      <VLKTabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "overview",     label: "Overview" },
          { value: "recovery",     label: "Recovery" },
          { value: "institutions", label: "Institutions" },
          { value: "geography",    label: "Geography" },
          { value: "risk",         label: "Risk & Compliance" },
          { value: "system",       label: "System health" },
        ]}
      />

      <div className="mt-6" data-testid={`an-tab-${tab}`}>
        {tab === "overview"     && <OverviewTab days={days} />}
        {tab === "recovery"     && <RecoveryTab />}
        {tab === "institutions" && <InstitutionsTab />}
        {tab === "geography"    && <GeographyTab />}
        {tab === "risk"         && <RiskComplianceTab days={days} />}
        {tab === "system"       && <SystemHealthTab />}
      </div>
    </div>
  );
}
